/* eslint-disable camelcase */
import React, { useCallback, useEffect, useState } from 'react'

import moment from 'moment'

import { Table } from '@tanstack/react-table'
import RadioButton from 'ageas-react-component-radiobutton'
import {
  InsuredPet,
  PackObjects,
  PacksObject,
  Premiums,
} from '../../@types/RunPetisSimulation'
import { useAnimalStore } from '../../Stores/animalStore'
import { useSimulatorStore } from '../../Stores/simulatorStore'
import { useAnimalValidateStore } from '../../Stores/useAnimalValidateStore'
import { useCreateProposalStore } from '../../Stores/useCreateProposalStore'
import { useGenerateProposalDocumentsStore } from '../../Stores/useGenerateProposalDocumentsStore'
import { useGetQuotationStore } from '../../Stores/useGetQuotationStore'
import { useResumeStore } from '../../Stores/useResumeStore'
import { useRunPetisSimulationStore } from '../../Stores/useRunPetisSimulationStore'
import { useWarningStore } from '../../Stores/warningStore'
import { MIN_PRICE } from '../../constants/minPrice'
import { PRICE_TYPE } from '../../constants/priceType'
import { WARNING_BAR } from '../../constants/warningBar'
import { useReferrals } from '../../hooks/useReferrals'
import { t } from '../../i18n'
import { currencyFormatter } from '../../utils/currencyFormatter'
import { Minus, Plus } from '../Icons'
import { RowsTableType } from '../SafeAnimalsPackageTable/InsuredObjects'
import { CoverageExpand, getCurrentAnimalExpandedKey } from './CoverageExpand'
import { CoverageTitle } from './CoverageTitle'
import { FormatterFieldCoverages } from './FormatterFieldCoverages'
import { Prices, findMinInsuredObjectTotalPremiumValue } from './Prices'
import {
  Actions,
  ActionsCell,
  AnimalName,
  ButtonContainer,
  Cell,
  ExpandHeader,
  ExpandTd,
  ExpandedRow,
  ExpandedRowContainer,
  NameCell,
  NamePackPrice,
  PackNameHeader,
  PackageTable,
  PackageTableCell,
  PackageTableHeader,
  PackageTableNameHeader,
  PackageTableRow,
  Row,
} from './styles'
import { useTotalPriceAnimalsInstallment } from './useTotalPriceAnimalsInstallment'

type AnimalTable = {
  animalId: string | null
  insuredObjectCode: string | null
} & InsuredPet

type PackUpdateAnimal = {
  name: string
  price: string
  installment: string
} & PacksObject

type TablePacksProps = {
  animal: AnimalTable
  tablePacks: Table<RowsTableType>
  tablePacksLength: number
  packsList: PackObjects[]
  index: number
  sequence: number
}

const DEFAULT = 'default'
const OPTIONAL = 'optional'
const COVERAGE_NAME = 'coverageName'

export const TablePacks = ({
  animal,
  tablePacks,
  tablePacksLength,
  packsList,
  index,
  sequence,
}: TablePacksProps) => {
  const { getQuotation } = useGetQuotationStore()
  const [expandPrice, setExpandPrice] = useState(false)
  const [columnSelected, setColumnSelected] = useState<string | null>()
  const [expandPriceItem, setExpandPriceItem] = useState<string | null>()
  const [currentPrice, setCurrentPrice] = useState<string>('')
  const [hasRecommended, setHasRecommended] = useState<string | boolean>(false)
  const [expandedRow, setExpandedRow] = useState(index === 0)
  const {
    setTotalPrice,
    typeOfPrice,
    setTypeOfPrice,
    setCurrentPack,
    currentPack,
    isRecalculate,
  } = useSimulatorStore()
  const { setWarningOptions } = useWarningStore()
  const { expandCoverageAnimal } = useAnimalValidateStore()
  const { referralsStop } = useReferrals()
  const { isResume, disabledStepsByResume } = useResumeStore()
  const { runPetisSimulation, isRequestLoading } = useRunPetisSimulationStore()
  const { isGenerateProposalDocuments } = useGenerateProposalDocumentsStore()
  const { hasCreatedProposal } = useCreateProposalStore()
  const { handleTotalPriceAnimalsInstallment } =
    useTotalPriceAnimalsInstallment()

  const totalPriceAnimalsInstallment = handleTotalPriceAnimalsInstallment(
    currentPack[animal.animalId as string],
  )

  const _animalList =
    runPetisSimulation?.runPetisSimulationResponse?.insuredObjectGroup
      ?.insuredObjects

  const isReferralStop = useCallback(
    (packId: string) =>
      (referralsStop || [])
        ?.map((referral) => referral?.packId)
        ?.includes(packId),
    [referralsStop],
  )

  // const disabledStep = isReportGenerated || isSentEmail
  const disabledStep = false

  const { updateAnimal, animalsList } = useAnimalStore()

  const handleExpandClick = () => {
    setExpandedRow((prev) => !prev)
  }

  const getMinPriceValue = (columnId: string) => {
    const totalPriceAnimalsInstallment = _animalList?.reduce(
      (acc, animal) =>
        acc +
        findMinInsuredObjectTotalPremiumValue(
          animal?.packsObject?.packs
            ?.find((pack) => pack.packId === columnId)
            ?.premiums?.filter(
              (premium) => premium.installment === typeOfPrice,
            ) || [],
        ),
      0,
    )

    return totalPriceAnimalsInstallment || 0
  }

  useEffect(() => {
    if (packsList) {
      const _columnSelected =
        (columnSelected || '')?.length === 0
          ? packsList?.find((pack: any) => pack.recommended)?.packId
          : columnSelected

      const findPack = packsList?.find(
        (pack) => pack.packId === _columnSelected,
      )

      const price = findPack?.premiums?.find(
        (premium) => premium.installment === typeOfPrice,
      )

      if (price) {
        updateAnimal(
          {
            name: animal.animalName || `Animal${index + 1}`,
            insuredObjectCode: animal.insuredObjectCode || '',
            pack: {
              name: _columnSelected,
              price: currencyFormatter(price?.invoiceRefPremium!),
              installment: typeOfPrice,
              sequence,
              ...findPack,
            } as PackUpdateAnimal,
          },
          animal.animalId!,
        )

        setCurrentPrice(currencyFormatter(price?.invoiceRefPremium!))
        setTotalPrice({
          [`animal-${animal.animalId}`]: Number(price?.invoiceRefPremium),
        })
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSelected, typeOfPrice, packsList])

  useEffect(() => {
    if (!packsList) return

    const packRecommended =
      packsList.find((pack) => pack.recommended)?.packId || ''
    const currentAnimal = animalsList.find(
      (_animal) => _animal.sequence === sequence,
    )
    const selectedPackId =
      currentPack[animal.animalId as string] ||
      currentAnimal?.pack?.packId ||
      packRecommended

    setColumnSelected(selectedPackId)
    setHasRecommended(packRecommended)
  }, [animal.animalId, currentPack, packsList, isResume])

  useEffect(() => {
    if (columnSelected) {
      setCurrentPack({
        [animal?.animalId as string]: columnSelected,
      })
    }
  }, [animal?.animalId, columnSelected, setCurrentPack])

  useEffect(() => {
    if (getQuotation?.policyQuotation?.quotationId && !isRequestLoading) {
      setExpandedRow(false)
    }
  }, [getQuotation])

  useEffect(() => {
    if (totalPriceAnimalsInstallment <= MIN_PRICE) {
      setWarningOptions({
        type: WARNING_BAR.type.info,
        labelTypes: [t('pages.step2.minPriceCampaign')],
      })
    }
  }, [totalPriceAnimalsInstallment])

  return (
    <React.Fragment>
      <Row>
        <NameCell>
          <AnimalName>{animal?.animalName}</AnimalName>
          <NamePackPrice>
            {isRecalculate || totalPriceAnimalsInstallment <= MIN_PRICE ? (
              <span>--</span>
            ) : (
              <>
                {currentPrice || 0}
                {typeOfPrice && (
                  <span>€/{PRICE_TYPE[typeOfPrice as never]}</span>
                )}
              </>
            )}
          </NamePackPrice>
        </NameCell>
        <Cell>
          {moment(new Date(animal?.animalBirthDate as string)).format(
            'DD/MM/YYYY',
          )}
        </Cell>
        <Cell>{columnSelected}</Cell>
        <Cell style={{ position: 'relative' }}>
          {animalsList[index]?.breed}
        </Cell>
        <ActionsCell>
          <Actions>
            <ButtonContainer onClick={() => handleExpandClick()}>
              {expandedRow ? <Minus /> : <Plus />}
            </ButtonContainer>
          </Actions>
        </ActionsCell>
      </Row>
      {expandedRow && (
        <ExpandedRowContainer>
          <ExpandedRow colSpan={5}>
            <div className="full-block-padding">
              <PackageTable>
                <thead className="full-width">
                  {tablePacks?.getHeaderGroups().map((headerGroup) => (
                    <PackageTableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header, index) => {
                        const content = packsList.find(
                          (item) => item.packId === header.id,
                        )

                        if (header.id === COVERAGE_NAME) {
                          return <PackageTableNameHeader key={index} />
                        }
                        return (
                          <PackageTableHeader
                            key={header.id}
                            onClick={() => {
                              if (
                                !isGenerateProposalDocuments &&
                                !disabledStepsByResume.step2 &&
                                !hasCreatedProposal
                              ) {
                                if (
                                  !isReferralStop(header.id) &&
                                  getMinPriceValue(header.id) >= MIN_PRICE
                                ) {
                                  setColumnSelected(header.id)
                                }
                              }
                            }}
                            selected={columnSelected === header.id}
                            isRecommended={hasRecommended === header.id}
                          >
                            {content?.recommended && (
                              <div className="full-block">
                                <div className="recommended">
                                  {t('pages.step2.table.recommended')}
                                </div>
                              </div>
                            )}
                            <div className="wrapper-pack-title">
                              <RadioButton
                                id={animal?.animalId + header.id}
                                key={animal?.animalId + header.id}
                                value={
                                  columnSelected === header.id ? header.id : ''
                                }
                                options={[
                                  {
                                    value: header.id,
                                    caption: '',
                                  },
                                ]}
                              />
                              <div className="pack-title-header">
                                <PackNameHeader>
                                  {content?.packName}
                                </PackNameHeader>
                              </div>
                            </div>
                            <Prices
                              disabled={
                                disabledStep ||
                                isGenerateProposalDocuments ||
                                disabledStepsByResume.step2 ||
                                hasCreatedProposal
                              }
                              isVisible={
                                expandPrice && expandPriceItem === header.id
                              }
                              packId={header.id}
                              prices={content?.premiums as Premiums[]}
                              headerId={header.id}
                              expandPriceItem={expandPriceItem as string}
                              currentPrice={currencyFormatter(
                                content?.premiums?.find(
                                  (item) => item.installment === typeOfPrice,
                                )?.invoiceRefPremium || 0,
                              )}
                              setExpandPriceItem={setExpandPriceItem}
                              onClick={(value) => {
                                setTypeOfPrice(value.toString())
                                setExpandPrice(false)
                              }}
                            />
                          </PackageTableHeader>
                        )
                      })}
                      <ExpandHeader />
                    </PackageTableRow>
                  ))}
                </thead>
                <tbody>
                  {tablePacks?.getRowModel().rows.map((row) => {
                    return (
                      <PackageTableRow key={row.id}>
                        {row.getVisibleCells().map((cell, indexCell) => {
                          if (
                            (row.original?.coverageId === DEFAULT ||
                              row.original?.coverageId === OPTIONAL) &&
                            cell.getContext().column.id === COVERAGE_NAME
                          ) {
                            return (
                              <CoverageTitle
                                key={indexCell}
                                isOptional={
                                  row.original?.coverageId === OPTIONAL
                                }
                              />
                            )
                          }

                          if (
                            row.original?.coverageId === DEFAULT ||
                            row.original?.coverageId === OPTIONAL
                          ) {
                            return (
                              <PackageTableCell
                                key={indexCell}
                                whiteBackground
                                isRecommended={
                                  hasRecommended === cell.getContext().column.id
                                }
                                lastItem={
                                  row.original?.coverageId === OPTIONAL &&
                                  expandCoverageAnimal?.[
                                    getCurrentAnimalExpandedKey(
                                      !row.original.mandatory!,
                                      animal?.animalId!,
                                    )
                                  ]
                                }
                                border={
                                  cell.getContext().column.id === columnSelected
                                }
                                onClick={() => {
                                  if (
                                    !isGenerateProposalDocuments &&
                                    !disabledStepsByResume.step2 &&
                                    !hasCreatedProposal &&
                                    cell.getContext().column.id !==
                                      COVERAGE_NAME
                                  ) {
                                    if (
                                      getMinPriceValue(
                                        cell.getContext().column.id,
                                      ) >= MIN_PRICE
                                    ) {
                                      setColumnSelected(
                                        cell.getContext().column.id,
                                      )
                                    }
                                  }
                                }}
                              />
                            )
                          }

                          return (
                            <PackageTableCell
                              key={cell.id}
                              hidden={
                                expandCoverageAnimal?.[
                                  getCurrentAnimalExpandedKey(
                                    !row.original.mandatory!,
                                    animal?.animalId!,
                                  )
                                ]
                              }
                              isRecommended={
                                hasRecommended === cell.getContext().column.id
                              }
                              border={
                                cell.getContext().column.id === columnSelected
                              }
                              lastItem={
                                cell.getContext().row.id ===
                                (tablePacksLength - 1).toString()
                              }
                              onClick={() => {
                                if (
                                  !isGenerateProposalDocuments &&
                                  !disabledStepsByResume.step2 &&
                                  !hasCreatedProposal &&
                                  cell.getContext().column.id !== COVERAGE_NAME
                                ) {
                                  if (
                                    getMinPriceValue(
                                      cell.getContext().column.id,
                                    ) >= MIN_PRICE
                                  ) {
                                    setColumnSelected(
                                      cell.getContext().column.id,
                                    )
                                  }
                                }
                              }}
                            >
                              <div
                                className="wrapper-table"
                                style={{
                                  justifyContent:
                                    cell.getContext().column.id ===
                                    COVERAGE_NAME
                                      ? 'space-between'
                                      : 'center',
                                }}
                              >
                                {cell.getContext().column.id ===
                                COVERAGE_NAME ? (
                                  (cell.getValue() as string)
                                ) : (
                                  <FormatterFieldCoverages
                                    animal={animalsList.find(
                                      (_animalList) =>
                                        _animalList.id === animal.animalId,
                                    )}
                                    fieldCoverages={
                                      cell.getContext().row.original
                                    }
                                    packId={cell.getContext().column.id}
                                    disabled={
                                      disabledStepsByResume.step2 ||
                                      hasCreatedProposal ||
                                      disabledStep ||
                                      isReferralStop(
                                        cell.getContext().column.id,
                                      ) ||
                                      cell.getContext().column.id !==
                                        columnSelected
                                    }
                                  />
                                )}
                              </div>
                            </PackageTableCell>
                          )
                        })}
                        {row.original?.coverageId === DEFAULT ||
                        row.original?.coverageId === OPTIONAL ? (
                          <ExpandTd whiteBackground>
                            <CoverageExpand
                              isOptional={row.original?.coverageId === OPTIONAL}
                              animalId={animal?.animalId!}
                            />
                          </ExpandTd>
                        ) : (
                          <ExpandTd
                            hidden={
                              expandCoverageAnimal?.[
                                getCurrentAnimalExpandedKey(
                                  !row.original.mandatory!,
                                  animal?.animalId!,
                                )
                              ]
                            }
                          />
                        )}
                      </PackageTableRow>
                    )
                  })}
                </tbody>
              </PackageTable>
            </div>
          </ExpandedRow>
        </ExpandedRowContainer>
      )}
    </React.Fragment>
  )
}
